export const PROFILE_FIELDS = Object.freeze([
  'bio',
  'location',
  'interests',
  'pronouns',
  'websites',
]);
export const PROFILE_VISIBILITY = Object.freeze(['public', 'friends', 'private']);
const limits = { name: 80, bio: 500, location: 100, interests: 200, pronouns: 60 };
const handlePattern = /^[a-z][a-z0-9_]{2,29}$/;

/** Additive local defaults. Returns a new object, preserves UUID/content, and reserves a
 * deterministic unique handle in the supplied set. Never verifies a seeded email. */
export function profileDefaults(user, takenHandles = new Set()) {
  let handle = user.handle?.toLowerCase();
  if (!handlePattern.test(handle ?? '') || takenHandles.has(handle)) {
    const base = String(user.name ?? 'member')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 22);
    const stem = /^[a-z]/.test(base) ? base : 'member';
    handle = stem.length >= 3 ? stem : `${stem}_member`;
    let suffix = 1;
    while (takenHandles.has(handle)) handle = `${stem}_${suffix++}`;
  }
  takenHandles.add(handle);
  return {
    ...user,
    handle,
    pronouns: user.pronouns ?? '',
    websites: user.websites ?? [],
    profileVisibility: {
      bio: 'public',
      location: 'public',
      interests: 'public',
      pronouns: 'private',
      websites: 'private',
      ...user.profileVisibility,
    },
    discoverableByEmail: user.discoverableByEmail === true,
    emailVerified: user.emailVerified === true,
  };
}

/** Validate explicit profile patches; throws TypeError on unknown fields or malformed values.
 * Lengths are JS string units. Identity UUID, email, verification and provider claims are immutable here. */
export function validateProfilePatch(input) {
  const allowed = new Set([
    'type',
    ...Object.keys(limits),
    'handle',
    'websites',
    'profileVisibility',
    'discoverableByEmail',
    'avatarImage',
    'coverImage',
    'avatarUpload',
    'coverUpload',
  ]);
  for (const key of Object.keys(input))
    if (input[key] !== undefined && !allowed.has(key))
      throw new TypeError('Unknown profile field.');
  const patch = {};
  for (const [key, max] of Object.entries(limits))
    if (input[key] !== undefined) {
      if (
        typeof input[key] !== 'string' ||
        input[key].length > max ||
        [...input[key]].some((character) => {
          const code = character.charCodeAt(0);
          return (code < 32 && ![9, 10, 13].includes(code)) || code === 127;
        }) ||
        (key === 'name' && !input[key].trim())
      )
        throw new TypeError(`Invalid ${key}.`);
      patch[key] = input[key].trim();
    }
  if (input.handle !== undefined) {
    if (typeof input.handle !== 'string' || !handlePattern.test(input.handle.toLowerCase()))
      throw new TypeError('Use 3–30 letters, numbers or underscores, starting with a letter.');
    patch.handle = input.handle.toLowerCase();
  }
  if (input.websites !== undefined) {
    if (!Array.isArray(input.websites) || input.websites.length > 3)
      throw new TypeError('Add up to three website links.');
    patch.websites = input.websites.map((value) => {
      if (typeof value !== 'string' || value.length > 2000)
        throw new TypeError('Invalid website link.');
      const url = new URL(value);
      if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password)
        throw new TypeError('Use an HTTP or HTTPS website without credentials.');
      return url.href;
    });
  }
  if (input.profileVisibility !== undefined) {
    const visibility = input.profileVisibility;
    if (
      !visibility ||
      typeof visibility !== 'object' ||
      Array.isArray(visibility) ||
      Object.entries(visibility).some(
        ([key, value]) => !PROFILE_FIELDS.includes(key) || !PROFILE_VISIBILITY.includes(value),
      )
    )
      throw new TypeError('Choose public, friends or private for profile fields.');
    patch.profileVisibility = { ...visibility };
  }
  if (input.discoverableByEmail !== undefined) {
    if (typeof input.discoverableByEmail !== 'boolean')
      throw new TypeError('Choose an email discovery preference.');
    patch.discoverableByEmail = input.discoverableByEmail;
  }
  for (const key of ['avatarImage', 'coverImage'])
    if (input[key] !== undefined) {
      if (input[key] !== null && typeof input[key] !== 'string')
        throw new TypeError('Choose an available profile image.');
      patch[key] = input[key];
    }
  for (const key of ['avatarUpload', 'coverUpload'])
    if (input[key] !== undefined) {
      const upload = input[key];
      if (
        !upload ||
        typeof upload !== 'object' ||
        Array.isArray(upload) ||
        Object.keys(upload).some((field) => !['data', 'width', 'height', 'alt'].includes(field)) ||
        typeof upload.data !== 'string' ||
        !/^data:image\/(jpeg|png|webp);base64,/.test(upload.data) ||
        upload.data.length > 3 * 1024 * 1024 ||
        !Number.isInteger(upload.width) ||
        !Number.isInteger(upload.height) ||
        upload.width < 1 ||
        upload.height < 1 ||
        Math.max(upload.width, upload.height) > 1600 ||
        (upload.alt !== undefined && (typeof upload.alt !== 'string' || upload.alt.length > 500))
      )
        throw new TypeError('Choose a processed profile photo within the size limit.');
      patch[key] = { ...upload };
    }
  return patch;
}

/** Whitelist projection, never object spreading. Optional fields honor bilateral friendship;
 * blocked viewers receive identity only. Own editable fields are visible regardless of audience. */
export function projectProfile(user, { self = false, friend = false, blocked = false } = {}) {
  const result = {
    id: user.id,
    name: user.name,
    handle: user.handle ?? '',
    bio: '',
    location: '',
    interests: '',
    pronouns: '',
    websites: [],
  };
  if (blocked && !self) return result;
  for (const field of PROFILE_FIELDS) {
    const visibility =
      user.profileVisibility?.[field] ??
      (['bio', 'location', 'interests'].includes(field) ? 'public' : 'private');
    if (self || visibility === 'public' || (visibility === 'friends' && friend))
      result[field] = user[field] ?? (field === 'websites' ? [] : '');
  }
  result.badges = (user.badges ?? [])
    .slice(0, 6)
    .map(({ label, emoji, image }) => ({ label, emoji, image }));
  result.isDemo = user.isDemo === true;
  result.isEditorial = user.isEditorial === true;
  result.avatarImage = user.avatarImage ?? null;
  result.coverImage = user.coverImage ?? null;
  if (self) {
    result.email = user.email;
    result.emailVerified = user.emailVerified === true;
    result.discoverableByEmail = user.discoverableByEmail === true;
    result.profileVisibility = { ...user.profileVisibility };
  }
  return result;
}

/** App-owned editorial pages can be followed but have no human friendship, personal inbox or Circle invitation flow. */
export function canReceivePersonalConnections(user) {
  return !!user && user.isEditorial !== true;
}
